import React, { PureComponent, Fragment } from 'react';
import { object, string, func, number } from 'prop-types';
import { Link } from 'react-router-dom';
import { history } from 'store';
import { graphql } from 'react-apollo';
import { display } from 'actions/banner';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import {
  BannerComponent,
  Button,
  Card,
  Dropdown,
  IconList,
  LevelNav,
  Modal,
  ProgressBar,
} from 'common/components';
import {
  ROUTE_CONTRACTS,
  ROUTE_CONTRACTS_EDIT,
  BANNER_IDS,
} from 'common/constants';
import { graphQlToFormErrors, capitalizeString } from 'common/utils';
import { MUTATION_DELETE_CONTRACT } from 'api/contract';
import * as icons from 'common/svg/icons';
import { date, toCommaNumber } from 'common/formatters';
import { SocialShare } from 'containers';
import { base64url_encode } from 'util/base64';

export class ContractDetails extends PureComponent {
  static displayName = 'ContractDetails';

  static propTypes = {
    contract: object,
    deleteContract: func,
    id: string,
    display: func,
    season: number,
  };

  static defaultProps = {
    deleteAction: () => null,
  };

  state = {
    showContractModal: false,
  };

  render() {
    const { contract } = this.props;
    const { company } = contract;

    return (
      <div className={'bg--primary'}>
        <BannerComponent
          theme={['strong', 'static', 'mobile-fixed']}
          message={
            <LevelNav
              nav={{
                left: {
                  element: (
                    <Link
                      className="chevron chevron--left chevron--invert"
                      to={ROUTE_CONTRACTS}
                    >
                      <span className={'visible--desktop'}>Contract List</span>
                    </Link>
                  ),
                },
                center: { label: contract && company },
                right: { element: this._getKebab() },
              }}
            />
          }
        />

        <div className={'column__inner'}>
          <section className={'cardlist__item'}>
            {this._getContractCard()}
          </section>
        </div>

        {this._getContractModal()}
      </div>
    );
  }

  _getContractCard = () =>
    this.props.contract && (
      <Card {...this._getCardData()}>{this._getCardChildren()}</Card>
    );

  _getCardData = () => {
    const { contract } = this.props;
    if (!contract) return null;

    return {
      title: contract.company,
      footer: contract.notes ? contract.notes : '',
    };
  };

  _getCardChildren = () => {
    const { quantity, contractNumber } = this.props.contract;
    const { delivered, contracted } = quantity;
    const list = this._getIconsArray();
    const metaList = this._getMetaArray();

    return (
      <Fragment>
        {!!contractNumber && (
          <h3 className={'cardlist__title'}>#{contractNumber}</h3>
        )}
        <IconList list={list} theme={['noborder']} />
        <ProgressBar
          value={delivered.size}
          max={contracted.size}
          unit={contracted.type && contracted.type.slice(0, 2)}
          label={{ free: 'Pending', used: 'Delivered' }}
        />
        <IconList list={metaList} />
      </Fragment>
    );
  };

  _getIconsArray = () => {
    const { crop } = this.props.contract;
    const list = [];

    crop &&
      list.push({
        icon: 'crop',
        label: `${crop.name} ${crop.variety ? crop.variety : ''}`,
      });
    return list;
  };

  _getMetaArray = () => {
    const {
      contractDate,
      deliveryDate,
      quantity,
      basePrice,
    } = this.props.contract;
    const { contracted } = quantity;
    const list = [];

    list.push(
      !!contractDate && { icon: 'calendar', label: date(contractDate) },
      !!deliveryDate && { icon: 'truck', label: date(deliveryDate) },
      !!contracted && {
        icon: 'storagefill',
        label: `${toCommaNumber(contracted.size)} ${capitalizeString(
          contracted.type
        )}`,
      },
      {
        icon: 'dollar',
        label: `${toCommaNumber(
          (basePrice.value * contracted.size).toFixed(2)
        )}`,
      },
      {
        icon: 'dollar',
        label: `${toCommaNumber(
          basePrice.value.toFixed(2)
        )} /${capitalizeString(contracted.type)}`,
      }
    );

    return list;
  };

  _getKebab = () => {
    const { contract, id, season } = this.props;

    return (
      <Fragment>
        <SocialShare id={id} season={season} />
        <Dropdown
          className="level-right"
          theme={['right', 'medium', 'invert']}
          trigger={<icons.kebab />}
        >
          <IconList
            list={[
              {
                label: 'Edit',
                icon: 'edit',
                action: () => {
                  history.push(
                    `${ROUTE_CONTRACTS_EDIT}/${base64url_encode(contract.id)}`
                  );
                },
              },
              {
                label: 'Delete',
                icon: 'garbage',
                action: () => this._toggleContractModal(),
              },
            ]}
            theme={['medium']}
          />
        </Dropdown>
      </Fragment>
    );
  };

  _toggleContractModal = () => {
    this.setState({ showContractModal: !this.state.showContractModal });
  };

  _getContractModal = () => {
    const { id, deleteContract } = this.props;
    return (
      this.state.showContractModal && (
        <Modal
          onToggle={() => this._toggleContractModal()}
          theme={['narrow']}
          header={'Delete Contract'}
        >
          <small>Are you sure you want to delete this contract?</small>
          <section className={'field__group'}>
            <fieldset className={'field text--center'}>
              <Button
                className="btn btn--primary"
                onClick={() => {
                  deleteContract(id);
                  this._toggleContractModal();
                }}
              >
                Yes
              </Button>
            </fieldset>
            <fieldset className={'field text--center'}>
              <Button
                className="btn"
                onClick={() => this._toggleContractModal()}
              >
                No
              </Button>
            </fieldset>
          </section>
        </Modal>
      )
    );
  };
}

const mapDispatch = dispatch => bindActionCreators({ display }, dispatch);
const mapState = ({ settings }) => ({ season: settings.season });
const enhance = compose(
  connect(
    mapState,
    mapDispatch
  ),
  graphql(MUTATION_DELETE_CONTRACT, {
    props: props => ({
      deleteContract: id => {
        return props
          .mutate({
            variables: { id },
          })
          .then(() => {
            props.ownProps.display(BANNER_IDS.APP, 'CONTRACT DELETED.');
            history.push(ROUTE_CONTRACTS);
          })
          .catch(graphQlToFormErrors);
      },
      ...props,
    }),
  })
);

export default enhance(ContractDetails);



// WEBPACK FOOTER //
// ./src/containers/ContractDetails/index.js